import Hls from "hls.js";
import { getConferencePlaybackTokenApiCall } from "../../API/conferences/conferencesService";

const PLAYBACK_TOKEN_REFRESH_LEAD_MS = 60 * 1000;
const PLAYBACK_TOKEN_RETRY_MS = 15 * 1000;

const PLAYBACK_STALL_CHECK_MS = 2000;
const PLAYBACK_STALL_RECOVERY_MS = 4000;

const withFreshToken = (url, token) => {
  if (!url || !token) return url;
  try {
    const u = new URL(url);
    if (!u.hostname.endsWith("mux.com")) return url;
    if (u.searchParams.has("signature")) return url;
    u.searchParams.set("token", token);
    return u.toString();
  } catch (_) {}
  return url;
};

const tokenFromUrl = (url) => {
  try {
    return new URL(url).searchParams.get("token");
  } catch (_) {
    return null;
  }
};

const jwtExpMs = (token) => {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload?.exp ? payload.exp * 1000 : null;
  } catch (_) {
    return null;
  }
};

const fetchPlaybackToken = (conferenceId, playbackId) =>
  new Promise((resolve, reject) => {
    getConferencePlaybackTokenApiCall(
      conferenceId,
      playbackId,
      (res) => resolve(res?.data ?? null),
      (err) => reject(err)
    );
  });

const makeTokenLoader = (tokenRef) =>
  class extends Hls.DefaultConfig.loader {
    load(context, config, callbacks) {
      context.url = withFreshToken(context.url, tokenRef.current);
      super.load(context, config, callbacks);
    }
  };

// timer (re-fetch ~60s before exp) and an on-403 safety-net reload
export const setupSignedHls = ({
  media,
  hlsRefSlot,
  tokenRef,
  conferenceId,
  playbackId,
  initialUrl,
  onManifestParsed,
  onFragChanged,
  onFatalNetwork,
  hlsOverrides,
}) => {
  let destroyed = false;
  let refreshTimer = null;
  let lastForcedReloadAt = 0;
  let applyToken = () => {};

  tokenRef.current = tokenFromUrl(initialUrl);

  const clearTimer = () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  };
  const scheduleFromToken = (token) => {
    clearTimer();
    const expMs = jwtExpMs(token);
    if (!expMs) return;
    const delay = Math.max(5000, expMs - Date.now() - PLAYBACK_TOKEN_REFRESH_LEAD_MS);
    refreshTimer = setTimeout(refresh, delay);
  };
  const refresh = async () => {
    if (destroyed || !conferenceId || !playbackId) return;
    try {
      const data = await fetchPlaybackToken(conferenceId, playbackId);
      if (destroyed || !data?.token) return;
      tokenRef.current = data.token;
      applyToken();
      scheduleFromToken(data.token);
    } catch (_) {
      if (!destroyed) {
        clearTimer();
        refreshTimer = setTimeout(refresh, PLAYBACK_TOKEN_RETRY_MS);
      }
    }
  };

  if (tokenRef.current) scheduleFromToken(tokenRef.current);
  else refresh();

  if (Hls.isSupported()) {
    const hls = new Hls({
      lowLatencyMode: true,
      maxLiveSyncPlaybackRate: 1.2,
      manifestLoadingMaxRetry: 20,
      manifestLoadingRetryDelay: 5000,
      manifestLoadingMaxRetryTimeout: 8000,
      loader: makeTokenLoader(tokenRef),
      ...hlsOverrides,
    });
    hlsRefSlot.current = hls;
    hls.loadSource(withFreshToken(initialUrl, tokenRef.current) || initialUrl);
    hls.attachMedia(media);
    if (onManifestParsed) hls.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);
    if (onFragChanged) hls.on(Hls.Events.FRAG_CHANGED, onFragChanged);
    hls.on(Hls.Events.ERROR, (_event, errData) => {
      if (errData?.response?.code === 403) {
        if (!errData.fatal) return;
        const now = Date.now();
        if (now - lastForcedReloadAt < PLAYBACK_TOKEN_RETRY_MS) return;
        lastForcedReloadAt = now;
        refresh().then(() => {
          if (!destroyed) hls.loadSource(withFreshToken(initialUrl, tokenRef.current) || initialUrl);
        });
        return;
      }
      if (errData.fatal && errData.type === Hls.ErrorTypes.MEDIA_ERROR) {
        hls.recoverMediaError();
        return;
      }
      if (errData.fatal && errData.type === Hls.ErrorTypes.NETWORK_ERROR) {
        if (onFatalNetwork) onFatalNetwork();
        setTimeout(() => {
          if (!destroyed) hls.loadSource(withFreshToken(initialUrl, tokenRef.current) || initialUrl);
        }, 5000);
      }
    });

    let lastProgress = 0;
    let lastProgressAt = Date.now();
    const stallWatch = setInterval(() => {
      if (destroyed || media.ended) return;
      if (media.paused) { media.play().catch(() => {}); return; }
      if (media.readyState < 2) return;
      if (media.currentTime > lastProgress + 0.1) {
        lastProgress = media.currentTime;
        lastProgressAt = Date.now();
        return;
      }
      if (Date.now() - lastProgressAt < PLAYBACK_STALL_RECOVERY_MS) return;
      lastProgressAt = Date.now();
      try {
        const live = hls.liveSyncPosition;
        if (Number.isFinite(live) && live > media.currentTime) {
          media.currentTime = live;
        } else if (media.buffered.length) {
          media.currentTime = media.buffered.end(media.buffered.length - 1);
        }
      } catch (_) {}
      hls.startLoad();
      media.play().catch(() => {});
    }, PLAYBACK_STALL_CHECK_MS);

    return () => {
      destroyed = true;
      clearTimer();
      clearInterval(stallWatch);
      hls.destroy();
      hlsRefSlot.current = null;
    };
  }

  if (media.canPlayType("application/vnd.apple.mpegurl")) {
    // Safari native HLS: no loader hook, so reapply src whenever the token rotates.
    applyToken = () => {
      media.src = withFreshToken(initialUrl, tokenRef.current) || initialUrl;
    };
    applyToken();
    media.play().catch(() => {});
  }

  return () => {
    destroyed = true;
    clearTimer();
  };
};
